import { Component } from '@theme/component';

const PREFERS_REDUCED_MOTION =
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HangingRailComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.track) return;

    this.track = this.refs.track;
    this.items = this.refs.items || [];
    this.dotsEl = this.refs.dots || null;

    this.liftIntoFocus = this.hasAttribute('data-lift-into-focus') && !PREFERS_REDUCED_MOTION;
    this.swingEnabled =
      !PREFERS_REDUCED_MOTION && parseFloat(getComputedStyle(this).getPropertyValue('--rail-swing')) > 0;

    this.activeDotIndex = -1;
    this.swingTimer = null;
    this.isPointerDown = false;
    this.didDrag = false;
    this.startX = 0;
    this.startScroll = 0;

    this.buildDots();
    this.bindRail();
    this.bindFocusView();
    this.observeEntrance();

    requestAnimationFrame(() => this.updateDots());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.entranceObserver?.disconnect();
    if (this.swingTimer) clearTimeout(this.swingTimer);
  }

  // ---------- rail: drag-to-scroll, hover, dots ----------

  bindRail() {
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.track.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.track.addEventListener('pointerup', () => this.onPointerUp());
    this.track.addEventListener('pointerleave', () => this.onPointerUp());
    this.track.addEventListener('pointercancel', () => this.onPointerUp());

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => item.classList.add('is-hovered'));
      item.addEventListener('mouseleave', () => item.classList.remove('is-hovered'));
      item.addEventListener('click', (event) => {
        if (this.didDrag) {
          event.preventDefault();
          this.didDrag = false;
          return;
        }
        this.openFocus(item);
      });
    });

    this.resizeObserver = new ResizeObserver(() => this.updateDots());
    this.resizeObserver.observe(this.track);
  }

  onPointerDown(event) {
    this.isPointerDown = true;
    this.didDrag = false;
    this.startX = event.clientX;
    this.startScroll = this.track.scrollLeft;
    this.track.classList.add('is-dragging');
    try {
      this.track.setPointerCapture(event.pointerId);
    } catch (e) {
      /* ignore */
    }
  }

  onPointerMove(event) {
    if (!this.isPointerDown) return;
    const delta = event.clientX - this.startX;
    if (Math.abs(delta) > 4) this.didDrag = true;
    this.track.scrollLeft = this.startScroll - delta;
  }

  onPointerUp() {
    this.isPointerDown = false;
    this.track.classList.remove('is-dragging');
  }

  onScroll() {
    this.triggerSwing();
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.updateDots();
      this.scrollTicking = false;
    });
  }

  triggerSwing() {
    if (!this.swingEnabled) return;
    this.items.forEach((item) => item.classList.add('is-swinging'));
    if (this.swingTimer) clearTimeout(this.swingTimer);
    this.swingTimer = setTimeout(() => {
      this.items.forEach((item) => item.classList.remove('is-swinging'));
    }, 1400);
  }

  buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';
    this.items.forEach(() => {
      const dot = document.createElement('span');
      this.dotsEl.appendChild(dot);
    });
  }

  updateDots() {
    if (!this.dotsEl || !this.items.length) return;
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;
    this.items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.left + rect.width / 2;
      const distance = Math.abs(itemCenter - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex === this.activeDotIndex) return;
    this.activeDotIndex = closestIndex;
    Array.from(this.dotsEl.children).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === closestIndex);
    });
  }

  observeEntrance() {
    if (!this.hasAttribute('data-drop-on-scroll') || PREFERS_REDUCED_MOTION) {
      this.classList.add('is-visible');
      return;
    }

    this.items.forEach((item, index) => {
      item.style.setProperty('--rail-drop-delay', `${Math.min(index * 60, 480)}ms`);
    });

    this.entranceObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('is-visible');
            this.entranceObserver.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    this.entranceObserver.observe(this);
  }

  // ---------- focus view ----------

  bindFocusView() {
    this.focus = this.refs.focus;
    if (!this.focus) return;

    this.refs.focusClose.addEventListener('click', () => this.closeFocus());
    this.refs.focusBackdrop.addEventListener('click', () => this.closeFocus());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.focus.hidden) this.closeFocus();
    });

    this.refs.focusTabs.querySelectorAll('button').forEach((tab) => {
      tab.addEventListener('click', () => this.setFocusView(tab.dataset.view));
    });

    this.refs.focusAdd.addEventListener('click', () => this.addToCart());
  }

  openFocus(item) {
    let data;
    try {
      data = JSON.parse(item.dataset.product || '{}');
    } catch (e) {
      data = {};
    }

    this.currentProduct = data;
    this.currentView = 'front';
    this.selectedVariantId = data.defaultVariantId || null;

    const index = this.items.indexOf(item);
    const total = this.items.length;
    const pad = (n) => String(n).padStart(2, '0');
    this.refs.focusCounter.textContent = `${pad(index + 1)} / ${pad(total)}`;
    this.refs.focusTitle.textContent = data.title || '';
    this.refs.focusPrice.textContent = data.price || '';
    this.refs.focusFooterTitle.textContent = data.title || '';
    this.refs.focusShopLink.textContent = data.linkText || 'Ver en la tienda';
    this.refs.focusShopLink.href = data.url || '#';
    this.refs.focusShopLink.hidden = !data.linkText;

    this.refs.focusTabs.querySelectorAll('button').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.view === 'front');
    });
    this.refs.focusImage.src = data.front || '';
    this.refs.focusImage.alt = data.title || '';

    this.buildSizes(data);

    this.refs.focusAdd.disabled = false;
    this.refs.focusAdd.textContent = 'Agregar al carrito';
    this.refs.focusViewCart.hidden = true;

    const sourceImg = item.querySelector('.rail-img--front') || item.querySelector('.rail-img--side');
    this.focus.hidden = false;
    requestAnimationFrame(() => {
      this.focus.classList.add('is-open');
    });

    if (this.liftIntoFocus && sourceImg) {
      this.playLiftAnimation(sourceImg);
    }

    document.body.style.overflow = 'hidden';
  }

  playLiftAnimation(sourceImg) {
    const startRect = sourceImg.getBoundingClientRect();
    if (!startRect.width || !startRect.height) return;

    const ghost = sourceImg.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.left = `${startRect.left}px`;
    ghost.style.top = `${startRect.top}px`;
    ghost.style.width = `${startRect.width}px`;
    ghost.style.height = `${startRect.height}px`;
    ghost.style.margin = '0';
    ghost.style.zIndex = '1001';
    ghost.style.opacity = '0.9';
    ghost.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease';
    ghost.style.pointerEvents = 'none';
    document.body.appendChild(ghost);

    requestAnimationFrame(() => {
      const targetRect = this.refs.focusImage.getBoundingClientRect();
      if (!targetRect.width) {
        ghost.remove();
        return;
      }
      const scaleX = targetRect.width / startRect.width;
      const scaleY = targetRect.height / startRect.height;
      const translateX = targetRect.left - startRect.left;
      const translateY = targetRect.top - startRect.top;
      ghost.style.transformOrigin = 'top left';
      ghost.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
      ghost.style.opacity = '0';
    });

    setTimeout(() => ghost.remove(), 480);
  }

  buildSizes(data) {
    const container = this.refs.focusSizes;
    container.innerHTML = '';

    if (data.hasOnlyDefaultVariant || !Array.isArray(data.variants) || data.variants.length < 2) {
      container.hidden = true;
      return;
    }

    container.hidden = false;
    let firstAvailableSelected = false;

    data.variants.forEach((variant) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = variant.label;
      btn.disabled = !variant.available;

      if (variant.available && !firstAvailableSelected) {
        btn.classList.add('is-selected');
        this.selectedVariantId = variant.id;
        firstAvailableSelected = true;
      }

      btn.addEventListener('click', () => {
        container.querySelectorAll('button').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.selectedVariantId = variant.id;
        this.refs.focusAdd.disabled = false;
        this.refs.focusAdd.textContent = 'Agregar al carrito';
        this.refs.focusViewCart.hidden = true;
      });

      container.appendChild(btn);
    });
  }

  setFocusView(view) {
    if (!this.currentProduct || view === this.currentView) return;
    this.currentView = view;
    this.refs.focusTabs.querySelectorAll('button').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.view === view);
    });
    const src = view === 'back' ? this.currentProduct.back : this.currentProduct.front;
    this.refs.focusImage.style.opacity = '0';
    setTimeout(() => {
      this.refs.focusImage.src = src || '';
      this.refs.focusImage.style.opacity = '1';
    }, 120);
  }

  async addToCart() {
    if (!this.selectedVariantId) return;
    this.refs.focusAdd.disabled = true;
    this.refs.focusAdd.textContent = 'Agregando…';

    try {
      const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.selectedVariantId, quantity: 1 }),
      });

      if (!response.ok) throw new Error('add to cart failed');

      this.refs.focusAdd.textContent = 'Agregado';
      this.refs.focusViewCart.hidden = false;
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    } catch (error) {
      this.refs.focusAdd.disabled = false;
      this.refs.focusAdd.textContent = 'Intentar de nuevo';
    }
  }

  closeFocus() {
    this.focus.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      this.focus.hidden = true;
    }, 350);
  }
}

customElements.define('hanging-rail-component', HangingRailComponent);